// Behaviour for the Investors / IPO page. Loaded once from investors-toc.liquid.
// Four modules: topic menu, DRHP disclaimer gate, content modal, Reg 46 jump.
// Modal/jump handlers are delegated on document (bind once, editor-safe);
// initToc() rebinds on shopify:section:load.
(() => {
  'use strict';

  // The <script> tag lives inside investors-toc.liquid, so reloading that
  // section in the Theme Editor re-executes this whole file. Guard so the
  // document-level handlers bind only once; the persistent
  // shopify:section:load listener (registered on the first run) re-runs the
  // menu setup for any freshly-loaded section.
  if (window.investorsPageInitialised) return;
  window.investorsPageInitialised = true;

  const TOC_DESKTOP_MEDIA_QUERY = '(min-width: 1024px)';
  const TOC_SIDEBAR_WIDTH_PX = 280;
  const TOC_GAP_PX = 32;
  const TOC_TOP_GAP_PX = 24;
  const SCROLLSPY_EXTRA_OFFSET_PX = 40;

  // Shared modal focus management: move focus into the dialog on open, trap Tab
  // inside it while open, and restore focus to the trigger on close.
  let modalFocusReturn = null;
  const modalFocusable = (modal) => [
    ...modal.querySelectorAll(
      'a[href], button:not([disabled]), textarea, input:not([disabled]), select, [tabindex]:not([tabindex="-1"])'
    ),
  ].filter((el) => el.offsetParent !== null);

  const openModal = (modal) => {
    modalFocusReturn = document.activeElement;
    modal.classList.add('is-open');
    const [first] = modalFocusable(modal);
    first?.focus();
  };

  const closeModal = (modal) => {
    modal.classList.remove('is-open');
    if (typeof modalFocusReturn?.focus === 'function') modalFocusReturn.focus();
    modalFocusReturn = null;
  };

  let focusTrapBound = false;
  const initFocusTrap = () => {
    if (focusTrapBound) return;
    focusTrapBound = true;
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab') return;
      const modal = document.querySelector('.investors-modal.is-open');
      if (!modal) return;
      const focusable = modalFocusable(modal);
      if (!focusable.length) return;
      const [first] = focusable;
      const last = focusable.at(-1);
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  };

  // Content modal: [data-content-modal-open] with data-content-target="<id>"
  // opens it from the hidden source with that id.
  let contentModalBound = false;
  const initContentModal = () => {
    if (contentModalBound) return;
    contentModalBound = true;

    document.addEventListener('click', (event) => {
      const modal = document.getElementById('investors-content-modal');
      const trigger = event.target.closest('[data-content-modal-open]');
      if (trigger) {
        event.preventDefault();
        if (!modal) return;
        const source = document.getElementById(trigger.dataset.contentTarget);
        if (!source) return;
        modal.querySelector('[data-content-modal-title]').textContent = source.dataset.title ?? '';
        modal.classList.toggle('investors-content-modal--bare', source.dataset.modalBare !== undefined);
        const body = modal.querySelector('[data-content-modal-body]');
        body.replaceChildren(...[...source.childNodes].map((node) => node.cloneNode(true)));
        body.scrollTop = 0;
        openModal(modal);
        return;
      }
      if (modal && (event.target === modal || event.target.closest('[data-content-modal-close]'))) {
        closeModal(modal);
      }
    });

    document.addEventListener('keydown', (event) => {
      const modal = document.getElementById('investors-content-modal');
      if (event.key === 'Escape' && modal?.classList.contains('is-open')) {
        closeModal(modal);
      }
    });
  };

  let drhpOnConfirm = null;
  let drhpBound = false;
  const getDrhpModal = () => document.getElementById('investors-drhp-modal');
  const closeDrhp = () => {
    drhpOnConfirm = null;
    const modal = getDrhpModal();
    if (modal) closeModal(modal);
  };
  // DRHP gate: window.investorsDrhpModal.open(onConfirm) runs onConfirm only
  // on "I Confirm"; decline/Escape/backdrop close and leave IPO closed.
  const initDisclaimerModal = () => {
    window.investorsDrhpModal = {
      open: (onConfirm) => {
        drhpOnConfirm = onConfirm;
        const modal = getDrhpModal();
        if (modal) openModal(modal);
      },
    };

    if (drhpBound) return;
    drhpBound = true;

    document.addEventListener('click', (event) => {
      if (event.target.closest('[data-drhp-confirm]')) {
        const callback = drhpOnConfirm;
        closeDrhp();
        callback?.();
        return;
      }
      if (event.target.closest('[data-drhp-decline]')) {
        closeDrhp();
        return;
      }
      const modal = getDrhpModal();
      if (modal && event.target === modal) closeDrhp();
    });

    document.addEventListener('keydown', (event) => {
      const modal = getDrhpModal();
      if (event.key === 'Escape' && modal?.classList.contains('is-open')) closeDrhp();
    });
  };

  // IPO video language toggle: [data-investors-video-lang] shows the matching
  // [data-investors-video-panel] within the same .investors-video-wrap and
  // hides its sibling. Hiding a panel also stops its media: a <video> is
  // paused directly (initVideoPlayer resets its overlay off the pause event);
  // an <iframe> (YouTube/Vimeo) has its src unloaded and restored on
  // switch-back, since neither stops playing from `hidden` alone.
  let videoLangBound = false;
  const initVideoLangToggle = () => {
    if (videoLangBound) return;
    videoLangBound = true;

    document.addEventListener('click', (event) => {
      const btn = event.target.closest('[data-investors-video-lang]');
      if (!btn) return;
      const wrap = btn.closest('.investors-video-wrap');
      if (!wrap) return;
      const lang = btn.dataset.investorsVideoLang;

      for (const otherBtn of wrap.querySelectorAll('[data-investors-video-lang]')) {
        const isActive = otherBtn === btn;
        otherBtn.classList.toggle('is-active', isActive);
        otherBtn.setAttribute('aria-pressed', String(isActive));
      }
      for (const panel of wrap.querySelectorAll('[data-investors-video-panel]')) {
        const shouldShow = panel.dataset.investorsVideoPanel === lang;
        panel.hidden = !shouldShow;
        const media = panel.querySelector('iframe, video');
        if (!media) continue;
        if (media.tagName === 'VIDEO') {
          if (!shouldShow) media.pause();
          continue;
        }
        if (shouldShow) {
          if (media.dataset.src) {
            media.src = media.dataset.src;
            delete media.dataset.src;
          }
        } else if (media.getAttribute('src')) {
          media.dataset.src = media.getAttribute('src');
          media.removeAttribute('src');
        }
      }
    });
  };

  // Minimal IPO video player (native `controls` are off, so play/pause, seek and
  // mute are the only affordances). data-state on the player wrapper cycles:
  //   idle    -> play icon only; click starts playback
  //   playing -> transparent click-catcher, nothing shown; click arms the UI
  //   armed   -> pause icon + control row (seek/mute); click pauses, or it
  //              reverts after a short delay — suspended while the seek track
  //              is being dragged so the row can't vanish mid-scrub
  // State is driven off the video's own play/pause/ended events rather than the
  // clicks, so a programmatic pause (e.g. the language toggle) also resyncs the
  // overlay. Media events don't bubble, hence the capture-phase listeners.
  // Touch needs longer: revealing the row and then aiming at a small target is
  // two deliberate taps, where a mouse is a single precise move.
  const VIDEO_PAUSE_HINT_MS = 1500;
  const VIDEO_PAUSE_HINT_TOUCH_MS = 3000;
  const isCoarsePointer = () => window.matchMedia('(hover: none)').matches;
  let videoPlayerBound = false;
  const initVideoPlayer = () => {
    if (videoPlayerBound) return;
    videoPlayerBound = true;

    const hintTimers = new WeakMap();
    const scrubbing = new WeakSet();

    const clearHint = (player) => {
      clearTimeout(hintTimers.get(player));
      hintTimers.delete(player);
    };

    const setState = (player, state) => {
      player.dataset.state = state;
      player
        .querySelector('[data-investors-video-toggle]')
        ?.setAttribute('aria-label', state === 'idle' ? 'Play' : 'Pause');
    };

    // Reverts `armed` back to `playing` unless the user is mid-scrub.
    const armHintTimer = (player, video) => {
      clearHint(player);
      hintTimers.set(
        player,
        setTimeout(
          () => {
            if (!video.paused && !scrubbing.has(player)) setState(player, 'playing');
          },
          isCoarsePointer() ? VIDEO_PAUSE_HINT_TOUCH_MS : VIDEO_PAUSE_HINT_MS
        )
      );
    };

    const partsFor = (el) => {
      const player = el.closest('[data-investors-video-player]');
      if (!player) return null;
      const video = player.querySelector('video');
      if (!video) return null;
      return { player, video, seek: player.querySelector('[data-investors-video-seek]') };
    };

    const renderSeek = (seek, video) => {
      if (!seek || !Number.isFinite(video.duration) || video.duration <= 0) return;
      seek.max = video.duration;
      seek.value = video.currentTime;
      seek.style.setProperty('--investors-seek-progress', `${(video.currentTime / video.duration) * 100}%`);
    };

    document.addEventListener('click', (event) => {
      const mute = event.target.closest('[data-investors-video-mute]');
      if (mute) {
        const parts = partsFor(mute);
        if (!parts) return;
        const { player, video } = parts;
        video.muted = !video.muted;
        mute.setAttribute('aria-pressed', String(video.muted));
        mute.setAttribute('aria-label', video.muted ? 'Unmute' : 'Mute');
        // Keep the row on screen rather than letting it vanish mid-interaction.
        if (player.dataset.state === 'armed') armHintTimer(player, video);
        return;
      }

      const toggle = event.target.closest('[data-investors-video-toggle]');
      if (!toggle) return;
      const parts = partsFor(toggle);
      if (!parts) return;
      const { player, video } = parts;

      const { state } = player.dataset;
      if (state === 'idle') {
        // Ignore rejections (e.g. a browser blocking playback) — the play
        // event never fires in that case, so the overlay stays on `idle`.
        video.play().catch(() => {});
        return;
      }
      if (state === 'playing') {
        setState(player, 'armed');
        armHintTimer(player, video);
        return;
      }
      clearHint(player);
      video.pause();
    });

    // Live scrub. `input` bubbles, so it can be delegated like the clicks.
    document.addEventListener('input', (event) => {
      const seek = event.target.closest?.('[data-investors-video-seek]');
      if (!seek) return;
      const parts = partsFor(seek);
      if (!parts) return;
      const { player, video } = parts;
      video.currentTime = Number(seek.value);
      renderSeek(seek, video);
      if (player.dataset.state === 'armed') armHintTimer(player, video);
    });

    // Hold the control row open for the whole drag, then restart the timer.
    document.addEventListener('pointerdown', (event) => {
      const seek = event.target.closest?.('[data-investors-video-seek]');
      if (!seek) return;
      const parts = partsFor(seek);
      if (!parts) return;
      scrubbing.add(parts.player);
      clearHint(parts.player);
    });

    for (const type of ['pointerup', 'pointercancel']) {
      document.addEventListener(type, () => {
        for (const player of document.querySelectorAll('[data-investors-video-player]')) {
          if (!scrubbing.has(player)) continue;
          scrubbing.delete(player);
          const video = player.querySelector('video');
          if (video && !video.paused) armHintTimer(player, video);
        }
      });
    }

    for (const type of ['play', 'pause', 'ended']) {
      document.addEventListener(
        type,
        (event) => {
          const video = event.target;
          if (!(video instanceof HTMLVideoElement)) return;
          const parts = partsFor(video);
          if (!parts) return;
          clearHint(parts.player);
          setState(parts.player, video.paused ? 'idle' : 'playing');
          renderSeek(parts.seek, video);
        },
        true
      );
    }

    for (const type of ['loadedmetadata', 'timeupdate', 'durationchange']) {
      document.addEventListener(
        type,
        (event) => {
          const video = event.target;
          if (!(video instanceof HTMLVideoElement)) return;
          const parts = partsFor(video);
          // Don't fight the user's drag by writing the track back mid-scrub.
          if (!parts || scrubbing.has(parts.player)) return;
          renderSeek(parts.seek, video);
        },
        true
      );
    }
  };

  // Reg 46 "View ->" jump: click the menu's own sub-topic button (one code
  // path for panel switching + IPO gating).
  let reg46Bound = false;
  const initReg46Jump = () => {
    if (reg46Bound) return;
    reg46Bound = true;

    document.addEventListener('click', (event) => {
      const jump = event.target.closest('[data-investors-jump]');
      if (!jump) return;
      event.preventDefault();
      const key = jump.dataset.investorsJump;
      const btn = document.querySelector(`[data-investors-subtopic="${key}"]`);
      btn?.click();
    });
  };

  // Topic menu: panel switching + self-contained sidebar positioning. Only
  // touches the menu's own element (and, on mobile, its wrapper's DOM order) —
  // never #main (see the layout note in investors.css).
  let tocState = null;
  const initToc = () => {
    const root = document.querySelector('.investors-toc-root');
    if (!root) return;

    if (tocState) {
      window.removeEventListener('scroll', tocState.onScroll);
      window.removeEventListener('resize', tocState.onResize);
      window.removeEventListener('load', tocState.onResize);
      tocState.desktopQuery.removeEventListener('change', tocState.onResize);
      tocState.spyObserver?.disconnect();
    }

    const menuWrapper = root.closest('.shopify-section') || root;
    const groups = [...root.querySelectorAll('.investors-menu__group')];
    // Keep in sync with the TOC_DESKTOP_MEDIA_QUERY sidebar layout in investors.css.
    const desktopQuery = window.matchMedia(TOC_DESKTOP_MEDIA_QUERY);

    const closeAllPanels = () => {
      for (const el of document.querySelectorAll('[data-investors-panel].is-open')) {
        el.classList.remove('is-open');
      }
    };

    const openPanel = (key) => {
      const panel = document.querySelector(`[data-investors-panel="${key}"]`);
      panel?.classList.add('is-open');
      return panel;
    };

    const getOpenPanels = () => [...document.querySelectorAll('[data-investors-panel].is-open')];

    // Opens all of `group`'s sub-topic panels (or its own panel for a leaf).
    const openTopicPanels = (group) => {
      const hasSublist = group.classList.contains('investors-menu__group--parent');
      const keys = [];
      if (hasSublist) {
        for (const btn of group.querySelectorAll('.investors-menu__subitem button[data-investors-subtopic]')) {
          keys.push(btn.dataset.investorsSubtopic);
        }
      } else {
        keys.push(group.dataset.investorsTopic);
      }
      const panels = [];
      for (const key of keys) {
        const panel = openPanel(key);
        if (panel) panels.push(panel);
      }
      return panels;
    };

    const clearActiveGroups = () => {
      for (const g of groups) {
        g.classList.remove('is-open');
        const h = g.querySelector('.investors-menu__group-header');
        if (h) {
          h.setAttribute('aria-current', 'false');
          h.setAttribute('aria-expanded', 'false');
        }
        for (const li of g.querySelectorAll('.investors-menu__subitem')) {
          li.classList.remove('is-active');
        }
      }
    };

    const scrollIntoViewSoon = (el) => {
      if (!el) return;
      requestAnimationFrame(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    };

    // Gates opening the IPO topic behind the disclaimer modal (only on the
    // closed->open transition, so switching its sub-topics doesn't re-prompt).
    const withDrhpGate = (group, proceed) => {
      const opening = !group.classList.contains('is-open');
      const isIpo = group.dataset.investorsTopic === 'ipo';
      if (opening && isIpo && window.investorsDrhpModal) {
        window.investorsDrhpModal.open(proceed);
      } else {
        proceed();
      }
    };

    const containerBounds = () => {
      // Reads the live inset straight off the OPEN panel's padding
      // (investors.css) — a closed panel is display:none and can't resolve
      // the percentage term in its padding, so it must never be used here.
      const panel = document.querySelector('.investors-panel.is-open') || document.querySelector('.investors-panel');
      const paddingLeft = panel ? parseFloat(getComputedStyle(panel).paddingInlineStart) : 0;
      return { left: paddingLeft - TOC_SIDEBAR_WIDTH_PX - TOC_GAP_PX };
    };

    const naturalTop = (scrollY) => {
      // Measure from menuWrapper, not `root` — once positioned, root's own rect
      // no longer reflects its natural flow position; the wrapper still does.
      return menuWrapper.getBoundingClientRect().top + scrollY;
    };

    const reserveSidebarSpace = (activePanel, sidebarHeight) => {
      // The absolute sidebar contributes 0 flow height, so the footer would
      // ride up under it — reserve its height on the panel driving the flow.
      for (const p of document.querySelectorAll('[data-investors-panel]')) {
        p.style.removeProperty('--investors-panel-min-height');
      }
      const placeholder = menuWrapper.querySelector('.investors-toc-placeholder');
      if (activePanel) {
        placeholder?.style.setProperty('--investors-toc-placeholder-height', '0px');
        activePanel.style.setProperty('--investors-panel-min-height', `${sidebarHeight}px`);
      } else if (placeholder) {
        placeholder.style.setProperty('--investors-toc-placeholder-height', `${sidebarHeight}px`);
      }
    };

    const updateDesktopPosition = () => {
      const openPanels = getOpenPanels();
      const scrollY = window.scrollY || window.pageYOffset;
      const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
      const bounds = containerBounds();
      // Clear any internal-scroll clamp before measuring, so offsetHeight reads
      // the menu's true (unclamped) height — reserveSidebarSpace/maxTop depend on it.
      root.style.removeProperty('--investors-toc-max-height');
      root.style.removeProperty('--investors-toc-overflow-y');
      const sidebarHeight = root.offsetHeight;

      // Keep `root` out of normal flow on desktop — its height would otherwise
      // inflate the measurement of whatever follows it. Do this BEFORE measuring
      // (on first load root is still static, so the panel below it reads too low
      // and the menu ends up stranded off-screen until the next recompute).
      root.style.setProperty('--investors-toc-left', `${bounds.left}px`);
      const currentPosition = root.style.getPropertyValue('--investors-toc-position');
      if (currentPosition !== 'absolute' && currentPosition !== 'fixed') {
        root.style.setProperty('--investors-toc-position', 'absolute');
      }

      if (!openPanels.length) {
        reserveSidebarSpace(null, sidebarHeight);
        root.style.setProperty('--investors-toc-position', 'absolute');
        root.style.setProperty('--investors-toc-top', `${naturalTop(scrollY)}px`);
        return;
      }

      // First pass (pre min-height): find the combined top and the naturally
      // last panel (tallest bottom) to reserve flow height on.
      let contentTop = Infinity;
      let lastPanel = openPanels[0];
      let lastBottom = -Infinity;
      for (const panel of openPanels) {
        const r = panel.getBoundingClientRect();
        const top = r.top + scrollY;
        const bottom = r.bottom + scrollY;
        if (top < contentTop) contentTop = top;
        if (bottom > lastBottom) {
          lastBottom = bottom;
          lastPanel = panel;
        }
      }

      reserveSidebarSpace(lastPanel, sidebarHeight);

      // Re-measure lastPanel now that min-height may have just stretched it.
      const contentBottom = lastPanel.getBoundingClientRect().bottom + scrollY;
      const stickTop = scrollY + headerHeight + TOC_TOP_GAP_PX;
      const maxTop = contentBottom - sidebarHeight;

      if (stickTop < contentTop) {
        root.style.setProperty('--investors-toc-position', 'absolute');
        root.style.setProperty('--investors-toc-top', `${contentTop}px`);
      } else if (stickTop < maxTop) {
        root.style.setProperty('--investors-toc-position', 'fixed');
        root.style.setProperty('--investors-toc-top', `${headerHeight + TOC_TOP_GAP_PX}px`);
        // A menu taller than the viewport would overflow off-screen while pinned
        // (its lower items unreachable) — cap it to the viewport and let it scroll
        // internally. Absolute states need no clamp: they move with the page.
        root.style.setProperty('--investors-toc-max-height', `calc(100vh - ${headerHeight + TOC_TOP_GAP_PX * 2}px)`);
        root.style.setProperty('--investors-toc-overflow-y', 'auto');
      } else {
        root.style.setProperty('--investors-toc-position', 'absolute');
        root.style.setProperty('--investors-toc-top', `${Math.max(contentTop, maxTop)}px`);
      }
    };

    const updateMobileOrder = () => {
      // Move each open panel's wrapper before the menu, preserving order.
      for (const panel of getOpenPanels()) {
        const wrapper = panel.closest('.shopify-section');
        if (wrapper && wrapper !== menuWrapper) {
          menuWrapper.parentNode.insertBefore(wrapper, menuWrapper);
        }
      }
    };

    const updateLayout = () => {
      if (desktopQuery.matches) {
        // Don't reset position to '' — that drops root into flow for a frame.
        // updateDesktopPosition() always sets an explicit position instead.
        updateDesktopPosition();
      } else {
        root.style.removeProperty('--investors-toc-position');
        root.style.removeProperty('--investors-toc-top');
        root.style.removeProperty('--investors-toc-left');
        root.style.removeProperty('--investors-toc-max-height');
        root.style.removeProperty('--investors-toc-overflow-y');
        updateMobileOrder();
      }
    };

    // Coalesce scroll recomputes to one per frame (updateLayout reads + writes
    // layout, so running it raw on every scroll event thrashes).
    let rafPending = false;
    const onScroll = () => {
      if (rafPending) return;
      rafPending = true;
      requestAnimationFrame(() => {
        rafPending = false;
        updateLayout();
      });
    };
    const onResize = () => {
      updateLayout();
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    window.addEventListener('load', onResize);
    desktopQuery.addEventListener('change', onResize);
    tocState = { onScroll, onResize, desktopQuery };

    // Scrollspy: as the reader scrolls the open topic's stacked panels, mark the
    // sub-topic whose panel is nearest the top of the viewport as active.
    const spyVisible = {};
    const highlightFromSpy = () => {
      const openParent = root.querySelector('.investors-menu__group--parent.is-open');
      if (!openParent) return;
      const items = [...openParent.querySelectorAll('.investors-menu__subitem')];
      let chosen = null;
      for (const li of items) {
        const b = li.querySelector('button[data-investors-subtopic]');
        if (b && !chosen && spyVisible[b.dataset.investorsSubtopic]) chosen = li;
      }
      if (!chosen) return;
      for (const li of items) li.classList.remove('is-active');
      chosen.classList.add('is-active');
    };
    const spyHeaderH = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
    const spyObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const key = entry.target.dataset.investorsPanel;
          if (entry.isIntersecting) spyVisible[key] = true;
          else delete spyVisible[key];
        }
        highlightFromSpy();
      },
      { rootMargin: `-${spyHeaderH + SCROLLSPY_EXTRA_OFFSET_PX}px 0px -55% 0px`, threshold: 0 }
    );
    for (const panel of document.querySelectorAll('[data-investors-panel]')) {
      spyObserver.observe(panel);
    }
    tocState.spyObserver = spyObserver;

    for (const group of groups) {
      // Bind each group's click handlers once. initToc() re-runs when any
      // section reloads in the editor; without this, a foreign reload would
      // re-run against these same nodes and stack duplicate handlers.
      if (group.investorsMenuBound) continue;
      group.investorsMenuBound = true;

      const header = group.querySelector('.investors-menu__group-header');

      if (header) {
        // Toggle the topic: open its stack (scrolled to the first panel), or
        // close it if its heading is clicked while already open.
        header.addEventListener('click', () => {
          const wasOpen = group.classList.contains('is-open');

          if (wasOpen) {
            closeAllPanels();
            clearActiveGroups();
            updateLayout();
            return;
          }

          withDrhpGate(group, () => {
            closeAllPanels();
            clearActiveGroups();
            group.classList.add('is-open');
            header.setAttribute('aria-current', 'true');
            header.setAttribute('aria-expanded', 'true');
            const panels = openTopicPanels(group);
            const firstSub = group.querySelector('.investors-menu__subitem');
            firstSub?.classList.add('is-active');
            scrollIntoViewSoon(panels[0] || group);
            updateLayout();
          });
        });
      }

      for (const btn of group.querySelectorAll('.investors-menu__subitem button')) {
        btn.addEventListener('click', () => {
          withDrhpGate(group, () => {
            closeAllPanels();
            clearActiveGroups();
            group.classList.add('is-open');
            if (header) {
              header.setAttribute('aria-current', 'true');
              header.setAttribute('aria-expanded', 'true');
            }

            openTopicPanels(group);
            btn.closest('.investors-menu__subitem').classList.add('is-active');

            const target = document.querySelector(`[data-investors-panel="${btn.dataset.investorsSubtopic}"]`);
            scrollIntoViewSoon(target);
            updateLayout();
          });
        });
      }
    }

    // Open the About panel by default so the content column is never blank on
    // load. Skip if something is already open (e.g. an editor section reload).
    if (!getOpenPanels().length) {
      const defaultGroup = root.querySelector('[data-investors-topic="about"]');
      if (defaultGroup) {
        defaultGroup.classList.add('is-open');
        const defaultHeader = defaultGroup.querySelector('.investors-menu__group-header');
        if (defaultHeader) {
          defaultHeader.setAttribute('aria-current', 'true');
          defaultHeader.setAttribute('aria-expanded', 'true');
        }
        openTopicPanels(defaultGroup);
      }
    }

    updateLayout();
  };

  const initAll = () => {
    initFocusTrap();
    initContentModal();
    initDisclaimerModal();
    initVideoLangToggle();
    initVideoPlayer();
    initReg46Jump();
    initToc();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll);
})();
